import React, { useState, useEffect, useCallback } from 'react';
import { Input, Select, InputNumber, Button, Card, Row, Col, message, Steps, Radio, Table, Typography, Alert, Space, Divider, Tag, Spin, Upload, Switch, AutoComplete, Tooltip } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, PlusOutlined, DeleteOutlined,
  UploadOutlined, StarFilled, StarOutlined, CalculatorOutlined,
  FilePdfOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import useAvisoAlteracoes from '../hooks/useAvisoAlteracoes';
import CabecalhoLaudo from '../components/CabecalhoLaudo';
import ManometroForm from '../components/ManometroForm';
import ValvulaForm from '../components/ValvulaForm';
import VidaRemanescente from '../components/VidaRemanescente';

const { Text } = Typography;
const { Option } = Select;

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const fmt = (n, c) => (n === null || n === undefined || !isFinite(Number(n))
  ? '—' : Number(n).toFixed(c === undefined ? 2 : c).replace('.', ','));
// Data ISO -> dd/mm/aaaa. O arquivo nao tinha formatador de data porque
// ate agora nenhuma etapa exibia data calculada.
const fmtData = (v) => (!v ? '—'
  : String(v).slice(0, 10).split('-').reverse().join('/'));
// As colunas *_bar guardam SEMPRE bar. A unidade e so como o valor foi
// digitado, entao o nome da coluna nunca mente e o calculo nao precisa
// saber de unidade nenhuma.
const UNIDADES_P = [{ value: 'bar', label: 'bar' }, { value: 'psi', label: 'psi' },
  { value: 'kgf', label: 'kgf/cm²' }, { value: 'mpa', label: 'MPa' }];
const FATOR_BAR = { bar: 1, psi: 0.0689476, kgf: 0.980665, mpa: 10 };
const emUnidade = (vBar, u) => (vBar === null || vBar === undefined || vBar === ''
  ? null : Number(vBar) / (FATOR_BAR[String(u || 'bar').toLowerCase()] || 1));
const paraBar = (v, u) => (v === null || v === undefined || v === ''
  ? null : Number(v) * (FATOR_BAR[String(u || 'bar').toLowerCase()] || 1));

const aj = (t) => <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2, lineHeight: 1.3 }}>{t}</div>;

const FORMAS = [['retangular', 'Retangular — Apêndice 13'], ['cilindrica', 'Cilíndrica — UG-27 e UG-32']];
const TIPOS_TAMPO = [['elipsoidal', 'Elipsoidal 2:1'], ['hemisferico', 'Hemisférico'], ['plano', 'Plano']];
const TIPOS_REFORCO = [
  ['u_fechado', 'Perfil U com as abas soldadas na chapa (caixa fechada)'],
  ['u_aberto', 'Perfil U com a alma soldada na chapa (abas para fora)'],
  ['barra', 'Barra chata soldada de topo'],
  ['cantoneira', 'Cantoneira'],
  ['manual', 'Outro — informar a seção na mão'],
];

// Onde a costela fica e se dá para medir. Em jaqueta com isolamento fechado
// não há medição nem inspeção visual direta: o laudo registra limitação de
// inspeção em vez de estimar número.
const ACESSO_REFORCO = [
  ['externo_acessivel', 'Por fora, acessível para medição'],
  ['jaqueta_fechada', 'Por fora, em jaqueta com isolamento fechado (inacessível)'],
  ['interno', 'Por dentro da câmara'],
];
const TIPOS_CICLO = [['gravitacional', 'Gravitacional — sem fase de vácuo'],
  ['pre_vacuo', 'Pré-vácuo (pulsante ou único)'],
  ['sfpp', 'SFPP — pulsos acima da atmosférica'],
  ['liquidos', 'Líquidos — exaustão lenta, sem vácuo']];
const TIPOS_FECHAMENTO = [['radial', 'Radial com volante'], ['deslizante', 'Deslizante / guilhotina'],
  ['bascula', 'Báscula'], ['aparafusada', 'Aparafusada'], ['outro', 'Outro']];
const TIPOS_JAQUETA = [['convencional', 'Convencional — casco envolvente (Apêndice 9)'],
  ['serpentina', 'Serpentina'], ['meia_cana', 'Meia-cana (Apêndice EE)']];
const CNA = [['conforme', 'Conforme'], ['nao_conforme', 'Não conforme'],
  ['nao_aplicavel', 'Não aplicável'], ['nao_verificado', 'Não verificado']];
// Rotulos curtos e cores do checklist: tres botoes cabem na linha,
// um clique por item em vez de abrir um Select.
const CNA_CURTO = { conforme: 'Conforme', nao_conforme: 'NC', nao_aplicavel: 'NA' };
const COR_CNA = { conforme: '#52c41a', nao_conforme: '#ff4d4f', nao_aplicavel: '#8c8c8c' };
const REGIOES = [['lado_longo', 'Lado longo'], ['lado_curto', 'Lado curto'],
  ['canto', 'Canto'], ['teto', 'Teto'], ['fundo', 'Fundo'], ['porta', 'Porta'],
  ['casco', 'Casco'], ['tampo', 'Tampo'], ['jaqueta', 'Jaqueta'],
  ['costela', 'Costela / reforço']];
const TIPOS_DISP = [['manovacuometro', 'Manovacuômetro'], ['pressostato', 'Pressostato'],
  ['quebra_vacuo', 'Válvula quebra-vácuo'], ['termometro', 'Termômetro'],
  ['indicador_nivel', 'Indicador de nível']];

const CHECKLIST = [
  ['Externo', 'Placa de identificação legível e completa', 'NR-13 13.5.1'],
  ['Externo', 'Pintura e proteção da superfície externa', ''],
  ['Externo', 'Corrosão externa do corpo', ''],
  ['Externo', 'Isolamento térmico — integridade e fixação', ''],
  ['Externo', 'Bases, suportes e fixação', ''],
  ['Externo', 'Aterramento elétrico', ''],
  ['Externo', 'Ventilação permanente — entradas de ar desobstruídas', 'NR-13 13.4.2'],
  ['Externo', 'Iluminação conforme normas oficiais vigentes', 'NR-13 13.4.2'],
  ['Externo', 'Sistema de iluminação de emergência', 'NR-13 13.4.2'],
  ['Externo', 'Tubulações conectadas — suportação e vazamentos', ''],
  ['Externo', 'Drenos e purgadores externos', ''],
  ['Externo', 'Acesso para operação e manutenção', ''],
  ['Externo', 'Sinalização e identificação de segurança', ''],
  ['Interno', 'Superfície interna da câmara', ''],
  ['Interno', 'Soldas internas', ''],
  ['Interno', 'Incrustação', ''],
  ['Interno', 'Corrosão localizada (pitting)', ''],
  ['Interno', 'Estado do fundo da câmara', ''],
  ['Interno', 'Dreno interno desobstruído', ''],
  ['Interno', 'Suportes e trilhos de carga internos', ''],
  ['Autoclave', 'Guarnição da porta — estado e vedação', 'UG-35.2'],
  ['Autoclave', 'Mecanismo de travamento da porta', 'UG-35.2(b)(1)'],
  ['Autoclave', 'Intertravamento de segurança da porta', 'UG-35.2(b)(2)'],
  ['Autoclave', 'Válvula quebra-vácuo — presença e funcionamento', 'UG-28(i)'],
  ['Autoclave', 'Filtro de ar da admissão', ''],
  ['Autoclave', 'Purgador de condensado', ''],
  ['Autoclave', 'Gerador de vapor acoplado', ''],
  ['Autoclave', 'Válvula de bloqueio da alimentação de vapor', ''],
  ['Autoclave', 'Reforços externos — integridade e soldas', 'UG-28(i)'],
];

export default function InspecaoCamara() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [fluidosNR13, setFluidosNR13] = useState([]);
  useEffect(() => { api.get('/classificacao-nr13/fluidos')
    .then((r) => setFluidosNR13(r.data || [])).catch(() => {}); }, []);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [d, setD] = useState({});
  const [materiais, setMateriais] = useState([]);
  const [medicoes, setMedicoes] = useState([]);
  const [disp, setDisp] = useState([]);
  const [chk, setChk] = useState([]);
  const naoSalvo = useAvisoAlteracoes([d, medicoes, disp, chk]);
  const [calculo, setCalculo] = useState(null);
  const [svg, setSvg] = useState('');
  const [imgs, setImgs] = useState({});
  const [possuiManometro, setPossuiManometro] = useState(false);
  const [possuiValvula, setPossuiValvula] = useState(false);

  const set = (campo, v) => setD((p) => ({ ...p, [campo]: v }));
  const soInterna = d.tipo_inspecao === 'interna';

  const miniatura = useCallback(async (idx) => {
    try {
      const r = await api.get('/inspecoes-camara/' + id + '/fotos/' + idx, { responseType: 'blob' });
      setImgs((p) => ({ ...p, [idx]: URL.createObjectURL(r.data) }));
    } catch (e) { /* foto ausente */ }
  }, [id]);

  const carregar = useCallback(async () => {
    try {
      const [l, m] = await Promise.all([
        api.get('/inspecoes-camara/' + id),
        api.get('/inspecoes-camara/apoio/materiais'),
      ]);
      setD(l.data);
      setMedicoes(l.data.medicoes || []);
      setDisp(l.data.dispositivos || []);
      // Laudos salvos antes de o check list crescer recebem os itens novos
      // sem perder o que ja foi respondido.
      const padrao = CHECKLIST.map(([grupo, item, ref], i) => ({
        grupo, item, referencia_norma: ref, resultado: 'nao_verificado',
        observacao: '', ordem: i }));
      const salvos = l.data.checklist || [];
      setChk(salvos.length
        ? salvos.concat(padrao.filter((x) => !salvos.some((y) => y.item === x.item)))
        : padrao);
      setCalculo(l.data.resultado_calculo || null);
      setMateriais(m.data);
      setPossuiManometro(!!l.data.possui_manometro);
      setPossuiValvula(!!l.data.possui_valvula);
      (l.data.fotos || []).forEach((f, i) => miniatura(i));
    } catch (e) { message.error('Não consegui carregar o laudo'); }
    finally { setCarregando(false); }
  }, [id, miniatura]);

  useEffect(() => { carregar(); }, [carregar]);

  // Volume interno a partir das dimensoes. So preenche quando o campo esta
  // vazio: havendo valor de placa, ele manda.
  const volumeL = (() => {
    if (d.forma === 'retangular') {
      const h = num(d.lado_longo_mm), H = num(d.lado_curto_mm), L = num(d.comprimento_mm);
      if (!(h > 0 && H > 0 && L > 0)) return null;
      return h * H * L / 1e6;
    }
    const D = num(d.diametro_interno_mm), L = num(d.comprimento_mm);
    if (!(D > 0 && L > 0)) return null;
    const corpo = Math.PI / 4 * D * D * L;
    const tampos = d.tipo_tampo === 'hemisferico' ? Math.PI * Math.pow(D, 3) / 6
      : d.tipo_tampo === 'elipsoidal' ? Math.PI * Math.pow(D, 3) / 12 : 0;
    return (corpo + tampos) / 1e6;
  })();
  // Espera a digitacao parar. Sem isso o preenchimento dispara no meio de um
  // numero pela metade - digitar "1600" passa por "16", a conta fecha em 6 L,
  // o campo deixa de estar vazio e nunca mais se corrige.
  useEffect(() => {
    if (!volumeL) return undefined;
    const t = setTimeout(() => {
      if (!num(d.capacidade_litros)) set('capacidade_litros', Math.round(volumeL));
    }, 900);
    return () => clearTimeout(t);
  }, [volumeL]); // eslint-disable-line react-hooks/exhaustive-deps

  // Croqui ao vivo: manda o estado atual, recebe o desenho. Nao grava nada.
  useEffect(() => {
    let vivo = true;
    const t = setTimeout(async () => {
      try {
        const r = await api.post('/inspecoes-camara/croqui/previa',
          { ...d, medicoes, calculo }, { responseType: 'text' });
        if (vivo) setSvg(typeof r.data === 'string' ? r.data : '');
      } catch (e) { /* previa e opcional */ }
    }, 400);
    return () => { vivo = false; clearTimeout(t); };
  }, [d.forma, d.lado_longo_mm, d.lado_curto_mm, d.comprimento_mm, d.raio_canto_mm,
      d.diametro_interno_mm, d.tipo_tampo, d.possui_reforco, d.reforco_passo_mm,
      d.reforco_altura_mm, medicoes, calculo]); // eslint-disable-line

  // Salvamento sempre explicito. Nunca automatico.
  const salvar = async () => {
    setSalvando(true);
    try {
      await api.put('/inspecoes-camara/' + id,
        { ...d, possui_manometro: possuiManometro, possui_valvula: possuiValvula });
      await api.put('/inspecoes-camara/' + id + '/medicoes', medicoes);
      await api.put('/inspecoes-camara/' + id + '/dispositivos', disp);
      await api.put('/inspecoes-camara/' + id + '/checklist', chk);
      naoSalvo.limpo();
      message.success('Laudo salvo');
    } catch (e) { message.error(e.response?.data?.erro || 'Erro ao salvar'); }
    finally { setSalvando(false); }
  };

  const calcular = async (irResultado) => {
    try {
      await salvar();
      const r = await api.post('/inspecoes-camara/' + id + '/calcular');
      setCalculo(r.data);
      // Sem isso o aviso da etapa 7 continuava na tela: ele le d.resultado_calculo,
      // que so era atualizado no proximo carregar().
      setD((p) => ({ ...p, resultado_calculo: r.data }));
      message.success('Cálculo concluído');
      // Resultado e a etapa 12 desde que a Vida remanescente entrou na 6.
      // O 11 antigo levava para Medicoes e fotos.
      if (irResultado !== false) setStep(13);
    } catch (e) { message.error(e.response?.data?.erro || 'Erro no cálculo'); }
  };

  // --------------------------------------------------------- previa do S
  const mat = materiais.find((m) => m.id === d.material_id);
  const curva = mat && mat.curvas && mat.curvas[d.serie_tensao || 'conservadora'];
  const interpolarS = (c, T) => {
    if (!c || !c.length || T === null) return null;
    if (T <= c[0][0]) return c[0][1];
    const u = c[c.length - 1];
    if (T > u[0]) return 'acima';
    for (let i = 1; i < c.length; i++) {
      if (T <= c[i][0]) {
        const [t0, s0] = c[i - 1], [t1, s1] = c[i];
        return s0 + ((T - t0) / (t1 - t0)) * (s1 - s0);
      }
    }
    return u[1];
  };
  const sPrevia = interpolarS(curva, num(d.temperatura_projeto_c));

  const previaTensao = () => {
    if (d.tensao_admissivel_mpa) return (
      <Alert type="info" showIcon style={{ marginTop: 8 }}
        message={'Valor digitado: S = ' + fmt(d.tensao_admissivel_mpa, 1) + ' MPa'}
        description="Campo preenchido na mão tem precedência sobre o cadastro." />);
    if (!mat) return null;
    if (!curva) return (
      <Alert type="warning" showIcon style={{ marginTop: 8 }}
        message="Material sem curva por temperatura"
        description={'Será usado o valor único do cadastro: ' + fmt(mat.tensao_mpa, 1) + ' MPa.'} />);
    if (sPrevia === 'acima') return (
      <Alert type="error" showIcon style={{ marginTop: 8 }}
        message="Temperatura acima da curva"
        description={'A curva vai até ' + fmt(curva[curva.length - 1][0], 0) + ' °C. O ASME não permite extrapolar.'} />);
    if (sPrevia === null) return aj('Informe a temperatura de projeto para ver o S.');
    return (
      <Alert type="success" showIcon style={{ marginTop: 8 }}
        message={'S = ' + fmt(sPrevia, 1) + ' MPa a ' + fmt(d.temperatura_projeto_c, 0) + ' °C'}
        description={'Interpolado na curva do ' + mat.nome + ', série '
          + (d.serie_tensao === 'alta_g5' ? 'alta (nota G5)' : 'conservadora')
          + '. Carta de pressão externa: ' + (mat.carta_pressao_externa || '—') + '.'} />);
  };

  // ------------------------------------------------------------- medicoes
  const addMedicao = () => setMedicoes((p) => p.concat([{
    regiao: 'lado_longo', posicao: '', ponto: p.length + 1, valor_mm: null }]));
  const setMed = (i, c, v) => setMedicoes((p) => p.map((x, k) => (k === i ? { ...x, [c]: v } : x)));
  const delMed = (i) => setMedicoes((p) => p.filter((_, k) => k !== i));

  // ---------------------------------------------------------------- fotos
  const subirFoto = async (file) => {
    setEnviando(true);
    try {
      const fd = new FormData(); fd.append('foto', file);
      const r = await api.post('/inspecoes-camara/' + id + '/fotos', fd);
      setD((p) => ({ ...p, fotos: r.data.lista }));
      setImgs({});
      (r.data.lista || []).forEach((f, i) => miniatura(i));
    } catch (e) { message.error('Erro ao enviar foto'); }
    finally { setEnviando(false); }
    return false;
  };
  const removerFoto = async (idx) => {
    try {
      const r = await api.delete('/inspecoes-camara/' + id + '/fotos/' + idx);
      setD((p) => ({ ...p, fotos: r.data.lista }));
      setImgs({});
      (r.data.lista || []).forEach((f, i) => miniatura(i));
    } catch (e) { message.error('Erro ao remover'); }
  };
  const marcarPrincipal = async (idx) => {
    try {
      const r = await api.put('/inspecoes-camara/' + id + '/fotos/' + idx + '/principal');
      setD((p) => ({ ...p, fotos: r.data.fotos }));
    } catch (e) { message.error('Erro'); }
  };

  // Memoria de calculo. Na etapa de Medicoes ela vai ABAIXO do conteudo
  // (digita as espessuras, depois calcula); na Vida remanescente vai ACIMA,
  // porque os numeros dela alimentam o que vem logo em seguida.
  const barraCalculo = () => {
        const rc = d.resultado_calculo || {};
        const em = rc.espessura_minima || {};
        return (
          <Card size="small" title="Memória de cálculo"
            style={{ marginBottom: 12, background: '#f6f8ff', borderColor: '#d0d7ff' }}
            extra={<Button type="primary" size="small" icon={<CalculatorOutlined />}
              onClick={() => calcular(false)}>Calcular</Button>}>
            {!rc.espessura_minima
              ? <Text type="secondary">Ainda não calculado. O botão salva o laudo e roda a
                  verificação sem sair desta etapa.</Text>
              : (<Row gutter={[16, 8]}>
                  <Col span={6}><Text type="secondary">PMTA calculada</Text><br />
                    <Text strong>{fmt(rc.interna && rc.interna.pmta_bar, 2)} bar</Text></Col>
                  <Col span={6}><Text type="secondary">Tensão admissível</Text><br />
                    <Text strong>{fmt(rc.tensao_admissivel_mpa, 1)} MPa</Text></Col>
                  <Col span={6}><Text type="secondary">Esp. mínima da chapa</Text><br />
                    <Text strong>{fmt(em.espessura_minima_mm, 3)} mm</Text></Col>
                  <Col span={6}><Text type="secondary">Menor esp. medida</Text><br />
                    <Text strong>{fmt(em.espessura_medida_mm, 3)} mm</Text></Col>
                  {em.parcela_reforco !== null && em.parcela_reforco !== undefined && (
                    <Col span={12}>{aj('O refor\u00e7o responde por '
                      + fmt(em.parcela_reforco * 100, 1) + '% da capacidade.')}</Col>)}
                  {rc.interna && rc.interna.governante && (
                    <Col span={12}>{aj('Verifica\u00e7\u00e3o governante: ' + rc.interna.governante)}</Col>)}
                  {rc.origem_espessura && (<Col span={24}>{aj('Espessura usada na verificação: '
                    + rc.origem_espessura + (rc.espessura_usada_mm
                      ? ' (' + fmt(rc.espessura_usada_mm, 3) + ' mm)' : ''))}</Col>)}
                  <Col span={24}>
                    <div style={{ borderTop: '1px solid #d0d7ff', paddingTop: 8, marginTop: 4 }}>
                      <Text strong>PMTA adotada pelo Profissional Habilitado</Text>
                      <Space.Compact style={{ width: 220, marginLeft: 12 }}>
                        <InputNumber style={{ width: '60%' }} decimalSeparator=","
                          value={emUnidade(num(d.pmta_adotada_bar), d.pmta_adotada_unidade || 'bar')}
                          onChange={(v) => set('pmta_adotada_bar', paraBar(v, d.pmta_adotada_unidade || 'bar'))} />
                        <Select style={{ width: '40%' }} value={d.pmta_adotada_unidade || 'bar'}
                          onChange={(u) => set('pmta_adotada_unidade', u)} options={UNIDADES_P} />
                      </Space.Compact>
                      {aj('Em branco, a referência é a PMTA de placa. Preenchida, ela manda na '
                        + 'espessura mínima, na vida remanescente e no laudo. '
                        + 'Clique em Calcular depois de alterar.')}
                    </div>
                  </Col>
                  <Col span={24}>
                    <div style={{ background: '#fff', border: '1px solid #e6eaf5',
                      borderRadius: 6, padding: '8px 10px', marginTop: 4, fontSize: 12,
                      lineHeight: 1.7 }}>
                      {rc.interna && (<>
                        <Text strong>Memória da PMTA</Text>
                        <div>{rc.interna.metodo} — ponto governante: {rc.interna.governante}</div>
                        {(() => {
                          const g = (rc.interna.verificacoes || [])
                            .find((v) => v.nome === rc.interna.governante);
                          if (!g) return null;
                          return (<div>P = limite do ponto ÷ coeficiente = {fmt(g.limite, 2)} MPa
                            ÷ {fmt(g.coef, 4)} = {fmt(g.limite / g.coef, 2)} MPa
                            = <Text strong>{fmt(g.pmax_bar, 2)} bar</Text></div>);
                        })()}
                        {rc.espessura_usada_mm && (<div>Espessura usada: {fmt(rc.espessura_usada_mm, 3)} mm
                          ({rc.origem_espessura})</div>)}
                        {em.secao && <div>Seção resistente: {em.secao}</div>}
                      </>)}
                      {rc.pv && (<>
                        <Text strong style={{ display: 'block', marginTop: 8 }}>Memória do P.V.</Text>
                        <div>P = {fmt(rc.pv.P_mpa, 4)} MPa ({fmt(rc.pv.P_kpa, 2)} kPa), pela
                          {' ' + (rc.origem_pmta_enquadramento || 'PMTA')}
                          {'  ·  '}V = {fmt(rc.pv.V_m3, 4)} m³</div>
                        <div>P.V. classificação = {fmt(rc.pv.P_mpa, 4)} × {fmt(rc.pv.V_m3, 4)}
                          = <Text strong>{fmt(rc.pv.PV_classificacao, 3)} MPa·m³</Text>
                          {rc.classificacao ? ' → Grupo ' + rc.classificacao.grupo : ''}</div>
                        <div>P.V. enquadramento = {fmt(rc.pv.PV_enquadramento, 2)} kPa·m³</div>
                        {rc.classificacao && (<div>Classe {rc.classificacao.classe_fluido}
                          {' + Grupo ' + rc.classificacao.grupo + ' → '}
                          <Text strong>Categoria {rc.classificacao.categoria}</Text></div>)}
                      </>)}
                    </div>
                  </Col>
                  <Col span={24}>{aj('Mem\u00f3ria completa \u2014 tabela do Ap\u00eandice 13, UG-28(i), '
                    + 'UG-35.2 e jaqueta \u2014 na etapa Resultado.')}</Col>
                </Row>)}
          </Card>);
      };

  const etapas = [
    { title: 'Identificação' },
    { title: 'Equipamento' },
    { title: 'Geometria' },
    { title: 'Material' },
    { title: 'Reforço' },
    { title: 'Vácuo e porta' },
    { title: 'Jaqueta' },
    { title: 'Medições' },
    { title: 'Vida remanescente' },
    { title: 'Check List' },
    { title: 'Manômetro' },
    { title: 'Válvula' },
    { title: 'Registro fotográfico' },
    { title: 'Resultado' },
  ];

  if (carregando) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  const ret = d.forma === 'retangular';

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Voltar</Button>
        <Text strong style={{ fontSize: 16 }}>
          Laudo de Inspeção {soInterna ? 'Interna' : 'Interna e Externa'} da Câmara
          de Esterilização {d.codigo ? '— ' + d.codigo : ''}
        </Text>
      </Space>

      <Steps size="small" labelPlacement="vertical" responsive={false} current={step} size="small" items={etapas} onChange={setStep}
        style={{ marginBottom: 16 }} />

      {/* O tipo de inspecao vem do modal da OS e nao se escolhe de novo aqui. */}
      {step === 8 && barraCalculo()}
      {step === 0 && <CabecalhoLaudo d={d} set={set} />}

      {step === 1 && (
        <Card title="Identificação do Equipamento">
          <Row gutter={[16, 16]}>
            <Col span={8}><Text>Tipo de Equipamento</Text>
              <AutoComplete style={{ width: '100%' }} placeholder="Escolha da lista ou digite"
                value={d.tipo_equipamento} onChange={(v) => set('tipo_equipamento', v)}
                options={['Autoclave', 'Autoclave Hospitalar', 'Autoclave Industrial', 'Câmara de Esterilização', 'Estufa de Esterilização'].map((t) => ({ value: t }))}
                filterOption={(e, o) => String(o.value).toLowerCase().includes(String(e).toLowerCase())} /></Col>
            <Col span={8}><Text>Tipo de Inspeção</Text>
              <Select style={{ width: '100%' }} value={d.tipo_inspecao_periodicidade || 'periodica'}
                onChange={(v) => set('tipo_inspecao_periodicidade', v)}
                options={[{ value: 'inicial', label: 'Inicial' }, { value: 'periodica', label: 'Periódica' }, { value: 'extraordinaria', label: 'Extraordinária' }]} /></Col>
            <Col span={8}><Text>Modo</Text>
              <Select style={{ width: '100%' }} disabled value={d.tipo_inspecao || 'interna_externa'}
                options={[{ value: 'interna', label: 'Interna' }, { value: 'interna_externa', label: 'Interna e Externa' }]} />
              {aj('Definido ao gerar o laudo a partir da OS e não muda depois. A inspeção só externa é um laudo próprio.')}</Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
            <Col span={4}><Text>TAG</Text>
              <Input value={d.tag} placeholder="AC-01"
                onChange={(e) => set('tag', (e.target.value || '').toUpperCase().replace(/^\s+/, '').replace(/\s\s+/g, ' '))}
                onBlur={(e) => set('tag', (e.target.value || '').trim())} /></Col>
            <Col span={4}><Text>Posição</Text>
              <Select style={{ width: '100%' }} value={d.posicao} onChange={(v) => set('posicao', v)}
                options={[{ value: 'vertical', label: 'Vertical' }, { value: 'horizontal', label: 'Horizontal' }]} /></Col>
            <Col span={8}><Text>Número de Série</Text>
              <Input value={d.numero_serie} onChange={(e) => set('numero_serie', e.target.value)} /></Col>
            <Col span={8}><Text>Fabricante</Text>
              <Input value={d.fabricante} onChange={(e) => set('fabricante', e.target.value)} /></Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
            <Col span={4}><Text>Fluido</Text>
              <Select style={{ width: '100%' }} showSearch optionFilterProp="label"
                value={d.fluido} onChange={(v) => {
                  set('fluido', v);
                  // A classe vem da tabela de fluidos do proprio ERP, nao de
                  // adivinhacao pelo nome. Em "outro" fica em branco para o PH
                  // escolher: sem isso cai no fallback 'D', a classe mais branda,
                  // e um inflamavel sairia com categoria menor e prazo maior.
                  const f = fluidosNR13.find((x) => x.nome === v);
                  set('classe_fluido', f ? f.classe : null);
                }}
                options={fluidosNR13.map((x) => ({ value: x.nome, label: x.nome + ' (Classe ' + x.classe + ')' })).concat([{ value: 'outro', label: 'Outro' }])} /></Col>
            <Col span={4}><Text>Fluido (outro)</Text>
              <Input value={d.fluido_outro} onChange={(e) => set('fluido_outro', e.target.value)} /></Col>
            <Col span={4}><Text>Classe do fluido (NR-13)</Text>
              <Select style={{ width: '100%' }} value={d.classe_fluido || undefined}
                placeholder="A, B, C ou D" onChange={(v) => set('classe_fluido', v)}
                options={[
                  { value: 'A', label: 'A — inflamáveis, H₂, acetileno, tóxicos ≤ 20 ppm' },
                  { value: 'B', label: 'B — combustíveis < 200 °C, tóxicos > 20 ppm' },
                  { value: 'C', label: 'C — vapor, asfixiantes simples, ar comprimido' },
                  { value: 'D', label: 'D — demais fluidos' }]} />
              {aj('Preenchida sozinha pelo fluido escolhido. Em "Outro", escolha aqui — '
                + 'em branco o sistema assume classe D, que é a mais branda e alonga o prazo.')}</Col>
            <Col span={4}><Text>Temperatura de operação (°C)</Text>
              <InputNumber style={{ width: '100%' }} decimalSeparator="," value={d.temperatura_operacao_c}
                onChange={(v) => set('temperatura_operacao_c', v)} /></Col>
            <Col span={4}><Text>Ano Fabricação</Text>
              <InputNumber style={{ width: '100%' }} min={1900} max={2100} value={d.ano_fabricacao}
                onChange={(v) => set('ano_fabricacao', v)} decimalSeparator="," /></Col>
            <Col span={4}><Text>Modelo</Text>
              <Input value={d.modelo} onChange={(e) => set('modelo', e.target.value)} /></Col>
            <Col span={4}><Text>ASME VIII, DIV. I</Text>
              <Input placeholder="Ex: tabela 2021" value={d.asm} onChange={(e) => set('asm', e.target.value)} /></Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
            <Col span={24}><Text>Local de Instalação</Text>
              <Input value={d.local_instalacao} onChange={(e) => set('local_instalacao', e.target.value)} /></Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
            <Col span={8}><Text>PMTA de placa</Text>
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber style={{ width: '62%' }} step={0.1} decimalSeparator=","
                  value={emUnidade(d.pmta_placa_bar, d.pmta_placa_unidade)}
                  onChange={(v) => set('pmta_placa_bar', paraBar(v, d.pmta_placa_unidade))} />
                <Select style={{ width: '38%' }} value={d.pmta_placa_unidade || 'bar'}
                  onChange={(u) => set('pmta_placa_unidade', u)} options={UNIDADES_P} />
              </Space.Compact></Col>
            <Col span={8}><Text>Pressão máxima de operação</Text>
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber style={{ width: '62%' }} step={0.1} decimalSeparator=","
                  value={emUnidade(d.pmo_bar, d.pmo_unidade)}
                  onChange={(v) => set('pmo_bar', paraBar(v, d.pmo_unidade))} />
                <Select style={{ width: '38%' }} value={d.pmo_unidade || 'bar'}
                  onChange={(u) => set('pmo_unidade', u)} options={UNIDADES_P} />
              </Space.Compact></Col>
          </Row>
          <Divider orientation="left" style={{ marginTop: 16 }}>Teste Hidrostático</Divider>
          <Row gutter={16} align="stretch" style={{ marginBottom: 12 }}>
            <Col span={12}>
              <div style={{ background: '#f6f8ff', border: '1px solid #d0d7ff', borderRadius: 8, padding: 12 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>TH anterior (fabricante ou outra inspetora)</Text>
                <Row gutter={16}>
                  <Col span={7}><Text>Possui registro?</Text>
                    <Select style={{ width: '100%' }} value={d.th_fabrica_possui}
                      onChange={(v) => set('th_fabrica_possui', v)}
                      options={[{ value: true, label: 'Sim' }, { value: false, label: 'Não' }]} /></Col>
                  {d.th_fabrica_possui && (<>
                    <Col span={6}><Text>Pressão do TH</Text>
                      <InputNumber style={{ width: '100%' }} decimalSeparator="," value={d.th_fabricante}
                        onChange={(v) => set('th_fabricante', v)} /></Col>
                    <Col span={5}><Text>Unidade</Text>
                      <Select style={{ width: '100%' }} value={d.th_fabrica_unidade || 'bar'}
                        onChange={(u) => set('th_fabrica_unidade', u)} options={UNIDADES_P} /></Col>
                    <Col span={6}><Text>Ano do TH</Text>
                      <InputNumber style={{ width: '100%' }} min={1900} max={2100} value={d.th_fabrica_ano}
                        onChange={(v) => set('th_fabrica_ano', v)} /></Col>
                  </>)}
                </Row>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ background: '#f6fff0', border: '1px solid #b7eb8f', borderRadius: 8, padding: 12 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>TH aplicado pela Celum</Text>
                <Row gutter={16}>
                  <Col span={8}><Text>Foi aplicado?</Text>
                    <Select style={{ width: '100%' }} value={d.th_celum_aplicado}
                      onChange={(v) => set('th_celum_aplicado', v)}
                      options={[{ value: true, label: 'Sim' }, { value: false, label: 'Não' }]} /></Col>
                  {d.th_celum_aplicado && (<>
                    <Col span={8}><Text>Data do ensaio</Text>
                      <Input type="date" value={String(d.data_th || '').slice(0, 10)}
                        onChange={(e) => set('data_th', e.target.value)} /></Col>
                    <Col span={5}><Text>Pressão</Text>
                      <InputNumber style={{ width: '100%' }} decimalSeparator="," value={d.th_celum_pressao}
                        onChange={(v) => set('th_celum_pressao', v)} /></Col>
                    <Col span={3}><Text>Un.</Text>
                      <Select style={{ width: '100%' }} value={d.th_celum_unidade || 'bar'}
                        onChange={(u) => set('th_celum_unidade', u)} options={UNIDADES_P} /></Col>
                  </>)}
                </Row>
              </div>
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginBottom: 4 }}>
            <Col span={12}><Text>Texto sobre TH no relatório</Text>
              <Select style={{ width: '100%' }} value={d.th_texto_opcao || 'sem_registro'}
                onChange={(v) => set('th_texto_opcao', v)}
                options={[
                  { value: 'sem_registro', label: 'Sem registro anterior — ensaio não aprovado pelo contratante' },
                  { value: 'com_registro', label: 'Possui registro de TH anterior — não submetido por nós' },
                  { value: 'registro_antigo', label: 'Último TH registrado com mais de 10 anos — recomendar novo TH' },
                  { value: 'realizado', label: 'TH realizado — relatório anexo' },
                  { value: 'custom', label: 'Texto próprio' }]} /></Col>
            {d.th_texto_opcao === 'custom' && (
              <Col span={12}><Text>Texto próprio</Text>
                <Input.TextArea rows={3} value={d.th_texto_custom}
                  onChange={(e) => set('th_texto_custom', e.target.value)} /></Col>)}
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
            <Col span={6}><Text>Instalação</Text>
              <Select style={{ width: '100%' }} value={d.instalacao_aberta} onChange={(v) => set('instalacao_aberta', v)}
                options={[{ value: true, label: 'Aberta' }, { value: false, label: 'Fechada' }]} /></Col>
            <Col span={6}><Text>Duas saídas amplas?</Text>
              <Select style={{ width: '100%' }} value={d.duas_saidas} onChange={(v) => set('duas_saidas', v)}
                options={[{ value: true, label: 'Sim' }, { value: false, label: 'Não' }]} /></Col>
            <Col span={6}><Text>Equipamento Móvel?</Text>
              <Select style={{ width: '100%' }} value={d.equipamento_movel} onChange={(v) => set('equipamento_movel', v)}
                options={[{ value: true, label: 'Sim' }, { value: false, label: 'Não' }]} /></Col>
            <Col span={6}><Text>Possui livro?</Text>
              <Select style={{ width: '100%' }} value={d.possui_livro} onChange={(v) => set('possui_livro', v)}
                options={[{ value: true, label: 'Sim' }, { value: false, label: 'Não' }]} /></Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
            <Col span={6}><Text>Possui prontuário?</Text>
              <Select style={{ width: '100%' }} value={d.possui_prontuario} onChange={(v) => set('possui_prontuario', v)}
                options={[{ value: true, label: 'Sim' }, { value: false, label: 'Não' }]} /></Col>
            <Col span={6}><Text>Enviar livro?</Text>
              <Select style={{ width: '100%' }} value={d.enviar_livro} onChange={(v) => set('enviar_livro', v)}
                options={[{ value: true, label: 'Sim' }, { value: false, label: 'Não' }]} /></Col>
            <Col span={6}><Text>Enviar prontuário?</Text>
              <Select style={{ width: '100%' }} value={d.enviar_prontuario} onChange={(v) => set('enviar_prontuario', v)}
                options={[{ value: true, label: 'Sim' }, { value: false, label: 'Não' }]} /></Col>
          </Row>
        </Card>)}

      {step === 2 && (
        <Card title="Geometria">
          <Radio.Group value={d.forma} onChange={(e) => set('forma', e.target.value)} style={{ marginBottom: 16 }}>
            {FORMAS.map(([k, r]) => <Radio.Button key={k} value={k}>{r}</Radio.Button>)}
          </Radio.Group>
          {aj('Retangular segue o Apêndice 13, que não tem fórmula fechada e calcula por pontos N, M e Q. Cilíndrica segue UG-27 e UG-32.')}
          <Row gutter={[16, 16]}>
            {ret ? (<>
              <Col span={8}><Text>Lado longo — <i>h</i> (mm)</Text>
                <InputNumber value={d.lado_longo_mm} style={{ width: '100%' }} onChange={(v) => set('lado_longo_mm', v)} decimalSeparator="," />
                {aj('Medida interna. No Apêndice 13 o lado longo é o h.')}</Col>
              <Col span={8}><Text>Lado curto — <i>H</i> (mm)</Text>
                <InputNumber value={d.lado_curto_mm} style={{ width: '100%' }} onChange={(v) => set('lado_curto_mm', v)} decimalSeparator="," />
                {aj('Medida interna. No Apêndice 13 o lado curto é o H — é invertido em relação à intuição, e trocar h com H troca todas as tensões.')}</Col>
              <Col span={8}><Text>Comprimento — <i>Lv</i> (mm)</Text>
                <InputNumber value={d.comprimento_mm} style={{ width: '100%' }} onChange={(v) => set('comprimento_mm', v)} decimalSeparator="," />
                {aj('Comprimento da câmara. Define a razão de aspecto Lv/h mostrada abaixo.')}</Col>
              <Col span={6}><Text>Raio de canto (mm)</Text>
                <InputNumber value={d.raio_canto_mm} style={{ width: '100%' }} onChange={(v) => set('raio_canto_mm', v)} decimalSeparator="," />
                {aj('Raio de concordância do canto. Zero se o canto for vivo.')}</Col>
              <Col span={6}><Text>Espessura lado curto — <i>t1</i> (mm)</Text>
                <InputNumber value={d.esp_nominal_lado_curto_mm} step={0.01} style={{ width: '100%' }} onChange={(v) => set('esp_nominal_lado_curto_mm', v)} decimalSeparator="," />
                {aj('Chapa do lado curto — o t1 das equações. É nela que a flexão do canto se concentra.')}</Col>
              <Col span={6}><Text>Espessura lado longo — <i>t2</i> (mm)</Text>
                <InputNumber value={d.esp_nominal_lado_longo_mm} step={0.01} style={{ width: '100%' }} onChange={(v) => set('esp_nominal_lado_longo_mm', v)} decimalSeparator="," />
                {aj('Chapa do lado longo — o t2 das equações.')}</Col>
              <Col span={6}><Text>Espessura da porta (mm)</Text>
                <InputNumber value={d.esp_nominal_porta_mm} step={0.01} style={{ width: '100%' }} onChange={(v) => set('esp_nominal_porta_mm', v)} decimalSeparator="," />
                {aj('Chapa plana da porta. Verificada por UG-34 com fator Z e C = 0,20, conforme 13-4(f).')}</Col>
              <Col span={24}>
                {num(d.comprimento_mm) > 0 && num(d.lado_longo_mm) > 0 && (
                  <Alert type={num(d.comprimento_mm) / num(d.lado_longo_mm) >= 4 ? 'success' : 'info'} showIcon
                    message={'Razão de aspecto Lv/h = ' + fmt(num(d.comprimento_mm) / num(d.lado_longo_mm), 2)}
                    description={num(d.comprimento_mm) / num(d.lado_longo_mm) >= 4
                      ? 'Dentro da faixa plena do Apêndice 13-4(h).'
                      : 'Abaixo de 4. O 13-4(h) diz que as equações continuam aplicáveis de forma conservadora.'} />)}
              </Col>
            </>) : (<>
              <Col span={8}><Text>Diâmetro interno (mm)</Text>
                <InputNumber value={d.diametro_interno_mm} style={{ width: '100%' }} onChange={(v) => set('diametro_interno_mm', v)} decimalSeparator="," />
                {aj('Medida interna do costado.')}</Col>
              <Col span={8}><Text>Comprimento (mm)</Text>
                <InputNumber value={d.comprimento_mm} style={{ width: '100%' }} onChange={(v) => set('comprimento_mm', v)} decimalSeparator="," />
                {aj('Comprimento entre linhas de tangência. É o L do cálculo de pressão externa.')}</Col>
              <Col span={8}><Text>Tipo de tampo</Text>
                <Select value={d.tipo_tampo} style={{ width: '100%' }} onChange={(v) => set('tipo_tampo', v)}>
                  {TIPOS_TAMPO.map(([k, r]) => <Option key={k} value={k}>{r}</Option>)}</Select>
                {aj('Define a fórmula do UG-32. Tampo plano cai no UG-34.')}</Col>
              <Col span={8}><Text>Espessura do casco (mm)</Text>
                <InputNumber value={d.esp_nominal_casco_mm} step={0.01} style={{ width: '100%' }} onChange={(v) => set('esp_nominal_casco_mm', v)} decimalSeparator="," />
                {aj('Espessura nominal do costado, antes de descontar a corrosão.')}</Col>
              <Col span={8}><Text>Espessura do tampo (mm)</Text>
                <InputNumber value={d.esp_nominal_tampo_mm} step={0.01} style={{ width: '100%' }} onChange={(v) => set('esp_nominal_tampo_mm', v)} decimalSeparator="," />
                {aj('Espessura nominal do tampo.')}</Col>
            </>)}
            <Col span={8}><Text>Capacidade</Text>
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber style={{ width: '62%' }} decimalSeparator=","
                  value={d.capacidade_unidade === 'm3'
                    ? (num(d.capacidade_litros) ? num(d.capacidade_litros) / 1000 : null)
                    : d.capacidade_litros}
                  onChange={(v) => set('capacidade_litros', v === null || v === undefined ? null
                    : (d.capacidade_unidade === 'm3' ? v * 1000 : v))} />
                <Space.Compact style={{ width: '38%' }}>
                  <Select style={{ width: '100%' }} value={d.capacidade_unidade || 'L'}
                    onChange={(u) => set('capacidade_unidade', u)}
                    options={[{ value: 'L', label: 'L' }, { value: 'm3', label: 'm³' }]} />
                </Space.Compact>
              </Space.Compact>
              {aj(volumeL ? 'Calculado das dimensões: ' + fmt(volumeL, 0) + ' L. Pode sobrescrever com o valor de placa.'
                : 'Preencha as dimensões e o volume é calculado sozinho.')}</Col>
            <Col span={8}><Text>Sobrespessura de corrosão (mm)</Text>
              <InputNumber value={d.sobrespessura_corrosao_mm} step={0.1} style={{ width: '100%' }} onChange={(v) => set('sobrespessura_corrosao_mm', v)} decimalSeparator="," />
                {aj('Descontada das espessuras e somada às dimensões internas antes de calcular. Zero quando o projeto não previu.')}</Col>
          </Row>
          <Divider orientation="left">Croqui</Divider>
          {svg
            ? <div style={{ overflowX: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: 8 }}
                dangerouslySetInnerHTML={{ __html: svg }} />
            : <Text type="secondary">Preencha as dimensões para ver o croqui.</Text>}
          {aj('Desenhado a partir dos campos acima. Não precisa carregar imagem.')}
        </Card>)}

      {step === 3 && (
        <Card title="Material e tensão admissível">
          <Row gutter={[16, 16]}>
            <Col span={10}><Text>Material</Text>
              <Select value={d.material_id} style={{ width: '100%' }} showSearch optionFilterProp="children"
                onChange={(v) => set('material_id', v)}>
                {materiais.map((m) => <Option key={m.id} value={m.id}>{m.nome}{m.tem_curva ? '' : '  (sem curva)'}</Option>)}
              </Select>
                {aj('Da tabela do sistema. "Sem curva" quer dizer que não há tensão por temperatura cadastrada — nesse caso preencha a tensão manual abaixo.')}</Col>
            <Col span={7}><Text>Série da Tabela 1A</Text>
              <Select value={d.serie_tensao || 'conservadora'} style={{ width: '100%' }} onChange={(v) => set('serie_tensao', v)}>
                <Option value="conservadora">Conservadora</Option>
                <Option value="alta_g5">Alta (nota G5)</Option></Select>
              {aj('A série G5 excede 2/3 do escoamento. O ASME não a recomenda onde pequena distorção cause vazamento — caso da porta com guarnição.')}</Col>
            <Col span={7}><Text>Temperatura de projeto (°C)</Text>
              <InputNumber value={d.temperatura_projeto_c} style={{ width: '100%' }} onChange={(v) => set('temperatura_projeto_c', v)} decimalSeparator="," />
                {aj('É esta que interpola o S na curva do material, não a de operação. O sistema recusa extrapolar acima da última temperatura tabelada.')}</Col>
            <Col span={24}>{previaTensao()}</Col>
            <Col span={8}><Text>Tensão admissível manual (MPa)</Text>
              <InputNumber value={d.tensao_admissivel_mpa} step={0.1} style={{ width: '100%' }} onChange={(v) => set('tensao_admissivel_mpa', v)} decimalSeparator="," />
              {aj('Deixe vazio para o sistema buscar na curva.')}</Col>
            <Col span={8}><Text>Limite de escoamento (MPa)</Text>
              <InputNumber value={d.limite_escoamento_mpa} step={1} style={{ width: '100%' }} onChange={(v) => set('limite_escoamento_mpa', v)} decimalSeparator="," />
                {aj('Usado no limite de 2/3 do escoamento, que vale para seção composta com reforço.')}</Col>
            <Col span={8}><Text>Eficiência de junta <i>E</i></Text>
              <InputNumber value={d.eficiencia_junta} step={0.05} min={0.1} max={1} style={{ width: '100%' }} onChange={(v) => set('eficiencia_junta', v)} decimalSeparator="," />
                {aj('Radiografia total 1,00; parcial 0,85; sem radiografia 0,70.')}</Col>
          </Row>
        </Card>)}

      {step === 4 && (
        <Card title="Reforço externo, estaiamento e ligamentos">
          <Alert type="info" showIcon style={{ marginBottom: 16 }}
            message="Por que isso importa"
            description="Numa câmara retangular a flexão no canto é mais de cem vezes maior que a tensão de membrana. O reforço externo carrega quase toda a capacidade — se estiver corroído ou com solda trincada, não sobra margem." />
          <Row gutter={[16, 16]}>
            <Col span={6}><Text>Possui reforço externo</Text>
              <Radio.Group value={d.possui_reforco} style={{ display: 'block', marginTop: 4 }} onChange={(e) => set('possui_reforco', e.target.value)}>
                <Radio value={true}>Sim</Radio><Radio value={false}>Não</Radio></Radio.Group></Col>
            {d.possui_reforco && (<>
              <Col span={6}><Text>Tipo de perfil</Text>
                <Select value={d.tipo_reforco} style={{ width: '100%' }} onChange={(v) => set('tipo_reforco', v)}>
                  {TIPOS_REFORCO.map(([k, r]) => <Option key={k} value={k}>{r}</Option>)}</Select></Col>
              <Col span={6}><Text>Acesso para medição</Text>
                <Select value={d.reforco_acesso || 'externo_acessivel'} style={{ width: '100%' }} onChange={(v) => set('reforco_acesso', v)}>
                  {ACESSO_REFORCO.map(([k, r]) => <Option key={k} value={k}>{r}</Option>)}</Select>
                {aj('Em jaqueta fechada o laudo registra limitação de inspeção e a saída normativa, em vez de estimar número.')}</Col>
              <Col span={6}><Text>Passo entre reforços (mm)</Text>
                <InputNumber value={d.reforco_passo_mm} style={{ width: '100%' }} onChange={(v) => set('reforco_passo_mm', v)} decimalSeparator="," /></Col>
              <Col span={6}><Text>Altura do perfil (mm)</Text>
                <InputNumber value={d.reforco_altura_mm} style={{ width: '100%' }} onChange={(v) => set('reforco_altura_mm', v)} decimalSeparator="," />
                {aj('Altura acima da chapa. A profundidade total da seção é esta altura mais a espessura da chapa.')}</Col>
              <Col span={6}><Text>Largura do perfil (mm)</Text>
                <InputNumber value={d.reforco_largura_mm} style={{ width: '100%' }} onChange={(v) => set('reforco_largura_mm', v)} decimalSeparator="," />
                {aj('Na barra chata, esta é a espessura da barra.')}</Col>
              <Col span={6}><Text>Parede do perfil (mm)</Text>
                <InputNumber value={d.reforco_espessura_mm} step={0.1} style={{ width: '100%' }} onChange={(v) => set('reforco_espessura_mm', v)} decimalSeparator="," />
                {aj('Espessura da chapa dobrada do perfil. Não se aplica a barra chata.')}</Col>
              <Col span={12}>
                <Alert type="success" showIcon style={{ marginBottom: 0 }}
                  message="Área, inércia e linha neutra são calculadas pelo sistema"
                  description="A partir do tipo, da altura, da largura e da parede. São contas de t³ com deslocamento de linha neutra, e precisam ser recalculadas a cada espessura testada na busca da espessura mínima — por isso saíram do campo digitado." />
              </Col>
              <Col span={6}><Text>Estado da solda costela-chapa</Text>
                <Select value={d.reforco_estado_solda} style={{ width: '100%' }} onChange={(v) => set('reforco_estado_solda', v)}>
                  {CNA.map(([k, r]) => <Option key={k} value={k}>{r}</Option>)}</Select>
                {aj('Item decisivo: sem essa solda transferindo cisalhamento, o Apêndice 13-8 não vale e a câmara cai para o valor da chapa sozinha.')}</Col>
              <Col span={6}><Text>Corrosão no reforço</Text>
                <Select value={d.reforco_corrosao} style={{ width: '100%' }} onChange={(v) => set('reforco_corrosao', v)}>
                  {CNA.map(([k, r]) => <Option key={k} value={k}>{r}</Option>)}</Select></Col>

              <Col span={24}><Divider orientation="left" plain style={{ margin: '4px 0' }}>
                Sobreposição manual da seção — deixe vazio no uso normal</Divider></Col>
              <Col span={6}><Text>Área do perfil (mm²)</Text>
                <InputNumber value={d.reforco_area_mm2} style={{ width: '100%' }} onChange={(v) => set('reforco_area_mm2', v)} decimalSeparator="," /></Col>
              <Col span={6}><Text>Inércia da seção composta (mm⁴)</Text>
                <InputNumber value={d.reforco_inercia_mm4} style={{ width: '100%' }} onChange={(v) => set('reforco_inercia_mm4', v)} decimalSeparator="," /></Col>
              <Col span={6}><Text>Linha neutra (mm)</Text>
                <InputNumber value={d.reforco_linha_neutra_mm} step={0.001} style={{ width: '100%' }} onChange={(v) => set('reforco_linha_neutra_mm', v)} decimalSeparator="," />
                {aj('Medida da face interna da chapa.')}</Col>
              <Col span={6}>
                {aj('Preenchendo os três, o sistema passa a usar esses valores e para de calcular a seção. Nesse modo a espessura mínima da costela não pode ser obtida, porque não sobra geometria para variar na busca.')}</Col>
            </>)}
            <Col span={24}><Divider style={{ margin: '8px 0' }} /></Col>
            <Col span={6}><Text>Possui estais (UG-47)</Text>
              <Radio.Group value={d.possui_estais} style={{ display: 'block', marginTop: 4 }} onChange={(e) => set('possui_estais', e.target.value)}>
                <Radio value={true}>Sim</Radio><Radio value={false}>Não</Radio></Radio.Group></Col>
            {d.possui_estais && (<>
              <Col span={6}><Text>Passo entre estais (mm)</Text>
                <InputNumber value={d.estai_passo_mm} style={{ width: '100%' }} onChange={(v) => set('estai_passo_mm', v)} decimalSeparator="," />
                {aj('Máximo de 220 mm pelo UG-47(f).')}</Col>
              <Col span={6}><Text>Diâmetro do estai (mm)</Text>
                <InputNumber value={d.estai_diametro_mm} style={{ width: '100%' }} onChange={(v) => set('estai_diametro_mm', v)} decimalSeparator="," /></Col>
              <Col span={6}><Text>Fator C</Text>
                <Select value={d.estai_fator_c} style={{ width: '100%' }} onChange={(v) => set('estai_fator_c', v)}>
                  <Option value={2.1}>2,1 — soldado, chapa ≤ 11 mm</Option>
                  <Option value={2.2}>2,2 — soldado, chapa &gt; 11 mm</Option>
                  <Option value={2.5}>2,5 — rosqueado com porca</Option>
                  <Option value={2.8}>2,8 — cabeça ≥ 1,3× diâmetro</Option>
                  <Option value={3.2}>3,2 — porcas e arruelas</Option></Select></Col>
            </>)}
            <Col span={24}><Divider style={{ margin: '8px 0' }} /></Col>
            <Col span={6}><Text>Possui furos / ligamentos</Text>
              <Radio.Group value={d.possui_furos} style={{ display: 'block', marginTop: 4 }} onChange={(e) => set('possui_furos', e.target.value)}>
                <Radio value={true}>Sim</Radio><Radio value={false}>Não</Radio></Radio.Group></Col>
            {d.possui_furos && (<>
              <Col span={6}><Text>Passo dos furos (mm)</Text>
                <InputNumber value={d.furo_passo_mm} style={{ width: '100%' }} onChange={(v) => set('furo_passo_mm', v)} decimalSeparator="," /></Col>
              <Col span={6}><Text>Diâmetro do furo (mm)</Text>
                <InputNumber value={d.furo_diametro_mm} style={{ width: '100%' }} onChange={(v) => set('furo_diametro_mm', v)} decimalSeparator="," /></Col>
            </>)}
          </Row>
        </Card>)}

      {step === 5 && (
        <Card title="Vácuo e porta">
          <Alert type="warning" showIcon style={{ marginBottom: 16 }}
            message="Vácuo acontece mesmo sem ciclo de vácuo"
            description="Câmara fechada cheia de vapor que esfria atinge cerca de 0,95 bar de pressão externa por condensação. A válvula quebra-vácuo é o que impede isso — por isso ela é dispositivo de segurança, não acessório." />
          <Row gutter={[16, 16]}>
            <Col span={8}><Text>Tipo de ciclo</Text>
              <Select value={d.tipo_ciclo} style={{ width: '100%' }} onChange={(v) => set('tipo_ciclo', v)}>
                {TIPOS_CICLO.map(([k, r]) => <Option key={k} value={k}>{r}</Option>)}</Select></Col>
            <Col span={4}><Text>Opera com vácuo</Text>
              <Radio.Group value={d.opera_com_vacuo} style={{ display: 'block', marginTop: 4 }} onChange={(e) => set('opera_com_vacuo', e.target.value)}>
                <Radio value={true}>Sim</Radio><Radio value={false}>Não</Radio></Radio.Group></Col>
            <Col span={4}><Text>Vácuo máximo (bar)</Text>
              <InputNumber value={d.vacuo_maximo_bar} step={0.01} style={{ width: '100%' }} onChange={(v) => set('vacuo_maximo_bar', v)} decimalSeparator="," /></Col>
            <Col span={8}><Text>Possui válvula quebra-vácuo</Text>
              <Select value={d.possui_quebra_vacuo} style={{ width: '100%' }} onChange={(v) => set('possui_quebra_vacuo', v)}>
                <Option value="sim">Sim</Option><Option value="nao">Não</Option>
                <Option value="nao_identificada">Não identificada</Option></Select></Col>
            <Col span={8}><Text>Quebra-vácuo testada</Text>
              <Radio.Group value={d.quebra_vacuo_testada} style={{ display: 'block', marginTop: 4 }} onChange={(e) => set('quebra_vacuo_testada', e.target.value)}>
                <Radio value={true}>Sim</Radio><Radio value={false}>Não</Radio></Radio.Group></Col>
            <Col span={8}><Text>Resultado do teste</Text>
              <Select value={d.quebra_vacuo_resultado} style={{ width: '100%' }} onChange={(v) => set('quebra_vacuo_resultado', v)}>
                {CNA.map(([k, r]) => <Option key={k} value={k}>{r}</Option>)}</Select></Col>
            <Col span={8}><Text>Comprovação de pressão externa</Text>
              <Select value={d.comprovacao_pressao_externa} style={{ width: '100%' }} onChange={(v) => set('comprovacao_pressao_externa', v)}>
                <Option value="prontuario_proof_test">Prontuário com ensaio de pressão</Option>
                <Option value="prontuario_sem">Prontuário sem o ensaio</Option>
                <Option value="sem_prontuario">Sem prontuário</Option></Select>
              {aj('UG-28(i): parte sob colapso exige estaiamento ou proof test.')}</Col>
          </Row>
          <Divider orientation="left">Porta — UG-35.2, Fechamento Rápido</Divider>
          <Row gutter={[16, 16]}>
            <Col span={6}><Text>Quantidade de portas</Text>
              <InputNumber value={d.quantidade_portas} min={1} max={2} style={{ width: '100%' }} onChange={(v) => set('quantidade_portas', v)} decimalSeparator="," /></Col>
            <Col span={6}><Text>Tipo de fechamento</Text>
              <Select value={d.tipo_fechamento} style={{ width: '100%' }} onChange={(v) => set('tipo_fechamento', v)}>
                {TIPOS_FECHAMENTO.map(([k, r]) => <Option key={k} value={k}>{r}</Option>)}</Select></Col>
            <Col span={6}><Text>Estado da guarnição</Text>
              <Select value={d.porta_estado_guarnicao} style={{ width: '100%' }} onChange={(v) => set('porta_estado_guarnicao', v)}>
                {CNA.map(([k, r]) => <Option key={k} value={k}>{r}</Option>)}</Select></Col>
            <Col span={6}><Text>Estado do travamento</Text>
              <Select value={d.porta_estado_travamento} style={{ width: '100%' }} onChange={(v) => set('porta_estado_travamento', v)}>
                {CNA.map(([k, r]) => <Option key={k} value={k}>{r}</Option>)}</Select></Col>
            {[['porta_travamento_engata_antes', 'Travamento engatado antes da pressurização e só libera com pressão aliviada', 'UG-35.2(b)(1)'],
              ['porta_falha_unica_segura', 'Falha de um único componente não abre, não vaza e não sobrecarrega os demais', 'UG-35.2(b)(2)'],
              ['porta_verificacao_visual', 'Condição dos elementos verificável por observação visual externa', 'UG-35.2(b)(3)'],
              ['porta_indicador_pressao', 'Indicador de pressão visível da área de operação', 'UG-35.2(b)(5)'],
              ['porta_alivio_antes_abertura', 'Pressão aliviada antes da porta poder ser totalmente aberta', 'UG-35.2(c)(1)(-b)'],
            ].map(([campo, texto, ref]) => (
              <Col span={24} key={campo}>
                <Row gutter={8} align="middle">
                  <Col flex="auto"><Text>{texto}</Text> <Tag>{ref}</Tag></Col>
                  <Col flex="220px">
                    <Select value={d[campo]} style={{ width: '100%' }} onChange={(v) => set(campo, v)}>
                      {CNA.map(([k, r]) => <Option key={k} value={k}>{r}</Option>)}</Select></Col>
                </Row>
              </Col>))}
            <Col span={24}><Text>Observações da porta</Text>
              <Input.TextArea rows={2} value={d.porta_observacoes} onChange={(e) => set('porta_observacoes', e.target.value)} /></Col>
          </Row>
          <Divider orientation="left">Outros dispositivos da câmara</Divider>
          {aj('Manômetro e válvula de segurança têm etapa própria.')}
          <Table size="small" pagination={false} rowKey={(_, i) => i} style={{ marginTop: 8 }}
            dataSource={disp.map((x, i) => ({ ...x, _i: i })).filter((x) => x.aplicacao !== 'jaqueta')}
            columns={[
              { title: 'Tipo', dataIndex: 'tipo', width: 200, render: (v, r) => (
                <Select value={v} style={{ width: '100%' }}
                  onChange={(x) => setDisp((p) => p.map((y, k) => (k === r._i ? { ...y, tipo: x } : y)))}>
                  {TIPOS_DISP.map(([k, nn]) => <Option key={k} value={k}>{nn}</Option>)}</Select>) },
              { title: 'TAG', dataIndex: 'tag', render: (v, r) =>
                <Input value={v} onChange={(e) => setDisp((p) => p.map((y, k) => (k === r._i ? { ...y, tag: e.target.value } : y)))} /> },
              { title: 'Faixa', dataIndex: 'faixa', render: (v, r) =>
                <Input value={v} onChange={(e) => setDisp((p) => p.map((y, k) => (k === r._i ? { ...y, faixa: e.target.value } : y)))} /> },
              { title: 'Resultado', dataIndex: 'resultado', width: 150, render: (v, r) => (
                <Select value={v} style={{ width: '100%' }}
                  onChange={(x) => setDisp((p) => p.map((y, k) => (k === r._i ? { ...y, resultado: x } : y)))}>
                  {CNA.map(([k, nn]) => <Option key={k} value={k}>{nn}</Option>)}</Select>) },
              { title: '', width: 50, render: (_, r) =>
                <Button danger size="small" icon={<DeleteOutlined />} onClick={() => setDisp((p) => p.filter((_, k) => k !== r._i))} /> },
            ]} />
          <Button icon={<PlusOutlined />} style={{ marginTop: 8 }}
            onClick={() => setDisp((p) => p.concat([{ aplicacao: 'camara', tipo: 'manovacuometro', resultado: 'conforme' }]))}>
            Adicionar dispositivo
          </Button>
        </Card>)}

      {step === 8 && (() => {
        const rc = d.resultado_calculo || {};
        const em = rc.espessura_minima;
        if (!em || !(em.espessura_minima_mm > 0)) return (
          <Alert type="warning" showIcon
            message="Rode o cálculo antes de calcular a vida remanescente"
            description={<>
              <div>A espessura mínima de uma câmara não sai de fórmula: o Apêndice 13
                é linear na pressão mas não na espessura, porque a inércia vai com t³ e a
                linha neutra se desloca. Ela é obtida por busca numérica.</div>
              <div style={{ marginTop: 6 }}>O botão abaixo salva o laudo e roda o cálculo
                sem sair desta etapa.</div>
              <Button type="primary" icon={<CalculatorOutlined />} style={{ marginTop: 10 }}
                onClick={() => calcular(false)}>Calcular agora</Button>
            </>} />
        );
        const pr = em.parcela_reforco;
        const fmtBar = (v) => (v === null || v === undefined ? '-' : Number(v).toFixed(2).replace('.', ',') + ' bar');
        const fmtMm = (v) => (v === null || v === undefined ? '-' : Number(v).toFixed(3).replace('.', ',') + ' mm');
        return (<>
          {rc.medicoes && rc.medicoes.aviso && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={rc.medicoes.aviso} />
          )}
          {em.atende === false && (
            <Alert type="error" showIcon style={{ marginBottom: 12 }}
              message={'Espessura medida abaixo da mínima: ' + fmtMm(em.espessura_medida_mm)
                + ' contra ' + fmtMm(em.espessura_minima_mm) + ' exigidos'}
              description="Com a espessura já abaixo da mínima a vida remanescente é zero ou negativa. O equipamento não sustenta a pressão de referência — isso é conclusão de laudo, não ajuste de prazo." />
          )}
          {pr !== null && pr !== undefined && pr > 0.5 && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }}
              message={'O reforço responde por ' + (pr * 100).toFixed(1) + '% da capacidade'}
              description="A vida remanescente abaixo é da chapa, que é o que a NR-13 pede. Mas com o reforço carregando esta fração, o item crítico deste equipamento é a costela: espessura da alma, estado da solda e corrosão por baixo do perfil. Registre isso nas recomendações." />
          )}
          <VidaRemanescente entrada={{
            valores: d,
            onChange: set,
            menorEspessura: em.espessura_medida_mm,
            espessuraMinima: em.espessura_minima_mm,
            anoFabricacao: d.ano_fabricacao,
            rotuloEquipamento: 'Câmara',
            rotuloPressao: fmtBar(em.pressao_alvo_bar)
              + (em.referencia_pressao ? ' — ' + em.referencia_pressao : ''),
            memoria: [
              em.metodo,
              'Espessura mínima para ' + fmtBar(em.pressao_alvo_bar) + ': ' + fmtMm(em.espessura_minima_mm)
                + ' (PMTA nessa espessura: ' + fmtBar(em.pmta_na_minima_bar) + ')',
              'Ponto governante: ' + (em.governante || '-'),
              rc.medicoes ? ('Menor medida: ' + fmtMm(rc.medicoes.menor_mm) + ' em "' + rc.medicoes.menor_regiao
                + '"' + (rc.medicoes.menor_canto_mm ? '; menor de canto: ' + fmtMm(rc.medicoes.menor_canto_mm) : '')) : null,
              em.secao ? 'Seção composta: ' + em.secao : null,
              pr !== null && pr !== undefined ? 'Parcela do reforço na capacidade: ' + (pr * 100).toFixed(1) + '%' : null,
              em.aviso || null,
            ].filter(Boolean),
            onResultado: (x) => {
              if (x.taxaReal !== null && x.taxaReal !== undefined)
                set('taxa_corrosao_mm_ano', Number(x.taxaReal.toFixed(4)));
              if (x.vrResidual !== null && x.vrResidual !== undefined)
                set('vida_remanescente_anos', Number(x.vrResidual.toFixed(1)));
            },
          }} />
        </>);
      })()}

      {step === 9 && (
        <Card title="Check List — Conforme | Não Conforme | Não Aplicável" size="small">
          {['Externo', 'Interno', 'Autoclave'].map((g) => (
            <div key={g}>
              <Divider orientation="left" style={{ margin: '12px 0 6px' }}>{g}</Divider>
              {chk.map((c, i) => (c.grupo === g ? (
                <Row key={i} gutter={16} align="middle"
                  style={{ marginBottom: 4, padding: '3px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Col span={11}><Text>{c.item}</Text>{c.referencia_norma ? <Tag style={{ marginLeft: 6 }}>{c.referencia_norma}</Tag> : null}</Col>
                  <Col span={7}>
                    <Space.Compact size="small">
                      {CNA.map(([k]) => (
                        <Button key={k} size="small"
                          style={c.resultado === k
                            ? { background: COR_CNA[k], borderColor: COR_CNA[k], color: '#fff' }
                            : undefined}
                          onClick={() => setChk((p) => p.map((x, n) => (n === i ? { ...x, resultado: k } : x)))}>
                          {CNA_CURTO[k] || k}</Button>))}
                    </Space.Compact></Col>
                  <Col span={6}>
                    <Input size="small" placeholder="observação" value={c.observacao || ''}
                      onChange={(e) => setChk((p) => p.map((x, k) => (k === i ? { ...x, observacao: e.target.value } : x)))} /></Col>
                </Row>) : null))}
            </div>))}
        </Card>)}

      {step === 10 && (
        <Card title="Manômetros" size="small">
          <div style={{ padding: 12, background: '#f0f4f8', borderRadius: 8, marginBottom: 16 }}>
            <Space><Text strong>O equipamento possui manômetro(s)?</Text>
              <Switch checked={possuiManometro} onChange={setPossuiManometro} checkedChildren="Sim" unCheckedChildren="Não" /></Space>
          </div>
          {possuiManometro && <ManometroForm laudoId={id ? parseInt(id, 10) : null}
            tagVaso={d.tag || ''} origem="camara" />}
        </Card>)}

      {step === 11 && (
        <Card title="Válvulas de Segurança" size="small">
          <div style={{ padding: 12, background: '#f0f4f8', borderRadius: 8, marginBottom: 16 }}>
            <Space><Text strong>O equipamento possui válvula(s) de segurança?</Text>
              <Switch checked={possuiValvula} onChange={setPossuiValvula} checkedChildren="Sim" unCheckedChildren="Não" /></Space>
          </div>
          {possuiValvula && <ValvulaForm laudoId={id ? parseInt(id, 10) : null}
            tagVaso={d.tag || ''} origem="camara" />}
        </Card>)}

      {step === 6 && (
        <Card title="Jaqueta">
          <Row gutter={[16, 16]}>
            <Col span={6}><Text>Possui jaqueta</Text>
              <Radio.Group value={d.possui_jaqueta} style={{ display: 'block', marginTop: 4 }} onChange={(e) => set('possui_jaqueta', e.target.value)}>
                <Radio value={true}>Sim</Radio><Radio value={false}>Não</Radio></Radio.Group></Col>
            {d.possui_jaqueta && (<>
              <Col span={9}><Text>Tipo</Text>
                <Select value={d.jaqueta_tipo} style={{ width: '100%' }} onChange={(v) => set('jaqueta_tipo', v)}>
                  {TIPOS_JAQUETA.map(([k, r]) => <Option key={k} value={k}>{r}</Option>)}</Select>
                {d.jaqueta_tipo === 'meia_cana' && aj('Meia-cana exige o fator K das Figuras EE, que o sistema ainda não tem. O laudo registra os dados e declara a pendência.')}</Col>
              <Col span={9}><Text>PMTA própria da jaqueta (bar)</Text>
                <InputNumber value={d.jaqueta_pmta_bar} step={0.1} style={{ width: '100%' }} onChange={(v) => set('jaqueta_pmta_bar', v)} decimalSeparator="," /></Col>
              <Col span={6}><Text>Diâmetro interno (mm)</Text>
                <InputNumber value={d.jaqueta_diametro_interno_mm} style={{ width: '100%' }} onChange={(v) => set('jaqueta_diametro_interno_mm', v)} decimalSeparator="," /></Col>
              <Col span={6}><Text>Espessura nominal (mm)</Text>
                <InputNumber value={d.jaqueta_esp_nominal_mm} step={0.01} style={{ width: '100%' }} onChange={(v) => set('jaqueta_esp_nominal_mm', v)} decimalSeparator="," /></Col>
              <Col span={6}><Text>Espessura medida (mm)</Text>
                <InputNumber value={d.jaqueta_esp_medida_mm} step={0.01} style={{ width: '100%' }} onChange={(v) => set('jaqueta_esp_medida_mm', v)} decimalSeparator="," /></Col>
              <Col span={6}><Text>Temperatura da jaqueta (°C)</Text>
                <InputNumber value={d.jaqueta_temperatura_c} style={{ width: '100%' }} onChange={(v) => set('jaqueta_temperatura_c', v)} decimalSeparator="," /></Col>
              <Col span={9}><Text>Material da jaqueta</Text>
                <Select value={d.jaqueta_material_id} style={{ width: '100%' }} showSearch optionFilterProp="children"
                  onChange={(v) => set('jaqueta_material_id', v)}>
                  {materiais.map((m) => <Option key={m.id} value={m.id}>{m.nome}</Option>)}</Select></Col>
              <Col span={6}><Text>Eficiência de junta</Text>
                <InputNumber value={d.jaqueta_eficiencia_junta} step={0.05} min={0.1} max={1} style={{ width: '100%' }}
                  onChange={(v) => set('jaqueta_eficiencia_junta', v)} decimalSeparator="," /></Col>
              <Col span={24}>
                <Divider orientation="left">Manômetros da jaqueta</Divider>
                <ManometroForm laudoId={id ? parseInt(id, 10) : null}
                  tagVaso={(d.tag || '') + ' — jaqueta'} origem="camara_jaqueta" />
                <Divider orientation="left">Válvulas de segurança da jaqueta</Divider>
                <ValvulaForm laudoId={id ? parseInt(id, 10) : null}
                  tagVaso={(d.tag || '') + ' — jaqueta'} origem="camara_jaqueta" />
              </Col>
            </>)}
          </Row>
        </Card>)}

      {step === 7 && (<>
        <Card title="Medição de espessura"
          extra={<Button icon={<PlusOutlined />} onClick={addMedicao}>Adicionar ponto</Button>}>
          {ret && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }}
              message="Não esqueça os cantos"
              description="O ponto que governa a PMTA numa câmara retangular é o canto, não o meio do vão. Medir só costado e tampo deixa de fora justamente a região crítica." />)}
          <Table size="small" pagination={false} rowKey={(_, i) => i} dataSource={medicoes}
            columns={[
              { title: 'Região', dataIndex: 'regiao', width: 150, render: (v, _, i) => (
                <Select value={v} style={{ width: '100%' }} onChange={(x) => setMed(i, 'regiao', x)}>
                  {REGIOES.map(([k, r]) => <Option key={k} value={k}>{r}</Option>)}</Select>) },
              { title: 'Posição', dataIndex: 'posicao', render: (v, _, i) =>
                <Input value={v} placeholder="ex.: canto superior esquerdo" onChange={(e) => setMed(i, 'posicao', e.target.value)} /> },
              { title: 'Ponto', dataIndex: 'ponto', width: 90, render: (v, _, i) =>
                <InputNumber value={v} style={{ width: '100%' }} onChange={(x) => setMed(i, 'ponto', x)} decimalSeparator="," /> },
              { title: 'Espessura (mm)', dataIndex: 'valor_mm', width: 140, render: (v, _, i) =>
                <InputNumber value={v} step={0.01} style={{ width: '100%' }} onChange={(x) => setMed(i, 'valor_mm', x)} decimalSeparator="," /> },
              { title: '', width: 50, render: (_, __, i) =>
                <Button danger size="small" icon={<DeleteOutlined />} onClick={() => delMed(i)} /> },
            ]} />
        </Card>
      </>)}

      {step === 12 && (
        <Card title="Registro fotográfico" size="small" style={{ marginTop: 12 }}
          extra={<Upload beforeUpload={subirFoto} showUploadList={false} accept="image/*" multiple>
            <Button icon={<UploadOutlined />} loading={enviando}>Adicionar foto</Button></Upload>}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {(d.fotos || []).map((f, i) => (
              <div key={i} style={{ border: '1px solid #ddd', borderRadius: 8, padding: 6, width: 160 }}>
                {imgs[i] ? <img src={imgs[i]} alt={f.nome} style={{ width: '100%', height: 110, objectFit: 'cover', borderRadius: 4 }} />
                  : <div style={{ height: 110, background: '#fafafa' }} />}
                <Space size={2} style={{ marginTop: 6 }}>
                  <Button size="small" icon={f.principal ? <StarFilled /> : <StarOutlined />}
                    title="Foto principal" onClick={() => marcarPrincipal(i)} />
                  <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removerFoto(i)} />
                </Space>
              </div>))}
          </div>
          {(d.fotos || []).length === 0 && <Text type="secondary">Nenhuma foto.</Text>}
        </Card>
      )}

      {step === 13 && (
        <Card title="Resultado"
          extra={<Space>
            <Button type="primary" icon={<CalculatorOutlined />} onClick={calcular}>Calcular</Button>
                      </Space>}>
          <Row gutter={16} style={{ marginBottom: 16 }}><Col span={6}>
            <label>Status do laudo</label>
            <Select style={{ width: '100%' }} value={d.status || 'rascunho'} onChange={(v) => set('status', v)}
              options={[{ value: 'rascunho', label: 'Rascunho' }, { value: 'concluido', label: 'Concluído' }]} />
          </Col></Row>
          {!calculo && <Alert type="info" showIcon message="Clique em Calcular para rodar a verificação." />}
          {calculo && (<>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={8}><Card size="small" title="PMTA calculada">
                <Text strong style={{ fontSize: 22 }}>{fmt(calculo.interna && calculo.interna.pmta_bar, 2)} bar</Text>
                {aj(calculo.interna && calculo.interna.metodo)}</Card></Col>
              <Col span={8}><Card size="small" title="PMTA de placa">
                <Text strong style={{ fontSize: 22 }}>{fmt(d.pmta_placa_bar, 2)} bar</Text></Card></Col>
              <Col span={8}><Card size="small" title="Tensão admissível usada">
                <Text strong style={{ fontSize: 22 }}>{fmt(calculo.tensao_admissivel_mpa, 1)} MPa</Text>
                {aj(calculo.fonte_tensao)}</Card></Col>
            </Row>
            {/* PMTA adotada: a decisao do Profissional Habilitado. Vira a
                pressao de referencia de tudo que depende de PMTA e e o numero
                impresso no laudo. Salvamento explicito, como o resto. */}
            <Card size="small" title="PMTA adotada pelo Profissional Habilitado"
              style={{ marginBottom: 16, borderColor: '#0099FF' }}>
              <Row gutter={16} align="middle">
                <Col span={6}>
                  <Space.Compact style={{ width: '100%' }}>
                    <InputNumber style={{ width: '60%' }} decimalSeparator=","
                      value={emUnidade(num(d.pmta_adotada_bar), d.pmta_adotada_unidade || 'bar')}
                      onChange={(v) => set('pmta_adotada_bar', paraBar(v, d.pmta_adotada_unidade || 'bar'))} />
                    <Select style={{ width: '40%' }} value={d.pmta_adotada_unidade || 'bar'}
                      onChange={(u) => set('pmta_adotada_unidade', u)} options={UNIDADES_P} />
                  </Space.Compact>
                </Col>
                <Col span={18}>{aj('Em branco, o c\u00e1lculo usa a PMTA de placa. Preenchida, '
                  + 'ela passa a ser a press\u00e3o de refer\u00eancia da espessura m\u00ednima, da vida '
                  + 'remanescente e do laudo. Clique em Calcular depois de alterar.')}</Col>
              </Row>
            </Card>
            {calculo.nota_aspecto && <Alert type="info" showIcon style={{ marginBottom: 12 }} message={calculo.nota_aspecto} />}
            {calculo.classificacao && (
              <Card size="small" title="Enquadramento NR-13 e prazos de inspeção"
                style={{ marginBottom: 16 }}>
                <Row gutter={[16, 8]}>
                  <Col span={4}><Text type="secondary">Classe do fluido</Text><br />
                    <Text strong style={{ fontSize: 18 }}>{calculo.classificacao.classe_fluido}</Text></Col>
                  <Col span={4}><Text type="secondary">Grupo</Text><br />
                    <Text strong style={{ fontSize: 18 }}>{calculo.classificacao.grupo}</Text></Col>
                  <Col span={4}><Text type="secondary">Categoria</Text><br />
                    <Text strong style={{ fontSize: 18 }}>{calculo.classificacao.categoria}</Text></Col>
                  <Col span={6}><Text type="secondary">Próxima inspeção externa</Text><br />
                    <Text strong>{calculo.validades
                      ? fmtData(calculo.validades.proxima_inspecao_externa) : '—'}</Text></Col>
                  <Col span={6}><Text type="secondary">Próxima inspeção interna</Text><br />
                    <Text strong>{calculo.validades
                      ? fmtData(calculo.validades.proxima_inspecao_interna) : '—'}</Text></Col>
                  {calculo.pv && (<Col span={24}>{aj('P.V. = '
                    + fmt(calculo.pv.PV_classificacao, 3) + ' MPa·m³ (classificação) e '
                    + fmt(calculo.pv.PV_enquadramento, 2) + ' kPa·m³ (enquadramento), pela '
                    + (calculo.origem_pmta_enquadramento || 'PMTA') + '.')}</Col>)}
                  {calculo.validades && calculo.validades.medidas_administrativas
                    && calculo.validades.medidas_administrativas.length > 0 && (
                    <Col span={24}><Alert type="warning" showIcon style={{ marginTop: 4 }}
                      message={'Prazo reduzido pelo item 13.5.2.5: '
                        + calculo.validades.medidas_administrativas.join('; ')} /></Col>)}
                </Row>
              </Card>)}

            {calculo.interna && calculo.interna.verificacoes && (<>
              <Divider orientation="left">Verificações do Apêndice 13</Divider>
              <Table size="small" pagination={false} rowKey="nome" dataSource={calculo.interna.verificacoes}
                columns={[
                  { title: 'Verificação', dataIndex: 'nome' },
                  { title: 'Limite (MPa)', dataIndex: 'limite', render: (v) => fmt(v, 1) },
                  { title: 'Pressão máxima (bar)', dataIndex: 'pmax_bar', render: (v, r) => (
                    <Text strong={r.nome === calculo.interna.governante}
                      type={r.nome === calculo.interna.governante ? 'danger' : undefined}>{fmt(v, 2)}</Text>) },
                ]} />
              {aj('Governante: ' + calculo.interna.governante)}
            </>)}
            {calculo.ug28i && (<>
              <Divider orientation="left">Pressão externa — UG-28(i)</Divider>
              <Alert showIcon type={calculo.ug28i.situacao === 'conforme' ? 'success'
                : calculo.ug28i.situacao === 'atencao' ? 'warning' : 'error'}
                message={'Situação: ' + String(calculo.ug28i.situacao).replace('_', ' ')}
                description={calculo.ug28i.texto} />
            </>)}
            {calculo.ug352 && (<>
              <Divider orientation="left">Porta — UG-35.2</Divider>
              <Table size="small" pagination={false} rowKey="item" dataSource={calculo.ug352.itens}
                columns={[
                  { title: 'Requisito', dataIndex: 'item' },
                  { title: 'Parágrafo', dataIndex: 'referencia', width: 170 },
                  { title: 'Resultado', dataIndex: 'resultado', width: 150, render: (v) =>
                    <Tag color={v === 'conforme' ? 'green' : v === 'nao_conforme' ? 'red' : 'default'}>
                      {String(v).replace(/_/g, ' ')}</Tag> },
                ]} />
            </>)}
            {calculo.jaqueta && (<>
              <Divider orientation="left">Jaqueta</Divider>
              {calculo.jaqueta.pendencia
                ? <Alert type="warning" showIcon message="Cálculo pendente" description={calculo.jaqueta.pendencia} />
                : <Text>PMTA da jaqueta: <Text strong>{fmt(calculo.jaqueta.pmta_bar, 2)} bar</Text> ({calculo.jaqueta.metodo})</Text>}
            </>)}

            {calculo.espessura_minima && (<>
              <Divider orientation="left">Espessura mínima e margem</Divider>
              {calculo.espessura_minima.erro
                ? <Alert type="warning" showIcon message="Espessura mínima não obtida"
                    description={calculo.espessura_minima.erro} />
                : (<Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
                    <Col span={12}><Card size="small" title="Chapa">
                      <Text>Mínima para {fmt(calculo.espessura_minima.pressao_alvo_bar, 2)} bar
                        {calculo.espessura_minima.referencia_pressao ? ' (' + calculo.espessura_minima.referencia_pressao + ')' : ''}:{' '}
                        <Text strong>{fmt(calculo.espessura_minima.espessura_minima_mm, 3)} mm</Text></Text><br />
                      <Text>Medida: <Text strong>{fmt(calculo.espessura_minima.espessura_medida_mm, 3)} mm</Text>
                        {calculo.espessura_minima.margem_mm !== null && calculo.espessura_minima.margem_mm !== undefined && (<>
                          {' '}— margem <Text strong type={calculo.espessura_minima.atende ? 'success' : 'danger'}>
                            {fmt(calculo.espessura_minima.margem_mm, 3)} mm</Text></>)}</Text>
                      {aj('Governa: ' + (calculo.espessura_minima.governante || '-'))}
                      {calculo.espessura_minima.secao && aj(calculo.espessura_minima.secao)}
                    </Card></Col>
                    {calculo.espessura_minima_reforco && (
                      <Col span={12}><Card size="small" title="Costela">
                        {calculo.espessura_minima_reforco.erro
                          ? <Text type="warning">{calculo.espessura_minima_reforco.erro}</Text>
                          : calculo.espessura_minima_reforco.dispensavel
                            ? <Text>{calculo.espessura_minima_reforco.observacao}</Text>
                            : (<>
                                <Text>Mínima na {calculo.espessura_minima_reforco.rotulo_variavel}:{' '}
                                  <Text strong>{fmt(calculo.espessura_minima_reforco.espessura_minima_mm, 3)} mm</Text>
                                  {' '}(hoje {fmt(calculo.espessura_minima_reforco.valor_atual_mm, 3)} mm)</Text><br />
                                <Text>Medida: <Text strong>{fmt(calculo.espessura_minima_reforco.espessura_medida_mm, 3)} mm</Text>
                                  {calculo.espessura_minima_reforco.margem_mm !== null && calculo.espessura_minima_reforco.margem_mm !== undefined && (<>
                                    {' '}— margem <Text strong type={calculo.espessura_minima_reforco.atende ? 'success' : 'danger'}>
                                      {fmt(calculo.espessura_minima_reforco.margem_mm, 3)} mm</Text></>)}</Text>
                                {aj('Sem o reforço a câmara cai para ' + fmt(calculo.espessura_minima_reforco.pmta_sem_reforco_bar, 2) + ' bar')}
                              </>)}
                      </Card></Col>)}
                  </Row>)}

              {calculo.espessura_minima.parcela_reforco > 0.5 && (
                <Alert type="warning" showIcon style={{ marginBottom: 8 }}
                  message={'O reforço responde por ' + (calculo.espessura_minima.parcela_reforco * 100).toFixed(1) + '% da capacidade'}
                  description="O prazo do laudo é o menor entre a vida remanescente da chapa e a da costela. Com o reforço carregando esta fração, o item crítico é a costela: espessura da alma, estado da solda e corrosão por baixo do perfil." />)}

              {calculo.espessura_minima_reforco && calculo.espessura_minima_reforco.nota_solda && (
                <Alert type="info" showIcon style={{ marginBottom: 8 }}
                  message="Solda costela-chapa" description={calculo.espessura_minima_reforco.nota_solda} />)}

              {calculo.medicoes && calculo.medicoes.aviso && (
                <Alert type="warning" showIcon style={{ marginBottom: 8 }} message={calculo.medicoes.aviso} />)}
              {calculo.espessura_minima_reforco && calculo.espessura_minima_reforco.aviso_medicao && (
                <Alert type="warning" showIcon style={{ marginBottom: 8 }} message={calculo.espessura_minima_reforco.aviso_medicao} />)}
            </>)}

            {calculo.limitacoes && calculo.limitacoes.length > 0 && (<>
              <Divider orientation="left">Limitações da inspeção</Divider>
              {calculo.limitacoes.map((x, i) => (
                <Alert key={i} type="error" showIcon style={{ marginBottom: 8 }}
                  message={x.item + ' — não inspecionado'}
                  description={<>
                    <div><Text strong>Motivo: </Text>{x.motivo}</div>
                    <div><Text strong>Consequência: </Text>{x.consequencia}</div>
                    <div><Text strong>Saída normativa: </Text>{x.saida_normativa}</div>
                    <div style={{ marginTop: 4 }}><Text type="secondary" style={{ fontSize: 12 }}>{x.norma}</Text></div>
                  </>} />))}
            </>)}

          </>)}
          <Divider />
          <Row gutter={[16, 16]}>
            <Col span={8}><Text>Conclusão</Text>
              <Select value={d.conclusao} style={{ width: '100%' }} onChange={(v) => set('conclusao', v)}>
                <Option value="aprovado">Aprovado</Option>
                <Option value="aprovado_com_restricao">Aprovado com restrição</Option>
                <Option value="reprovado">Reprovado</Option></Select></Col>
            {!soInterna && (
              <Col span={8}><Text>Próxima inspeção externa</Text>
                <Input type="date" value={d.data_proxima_externa || ''}
                  onChange={(e) => set('data_proxima_externa', e.target.value)} /></Col>)}
            <Col span={8}><Text>Próxima inspeção interna</Text>
              <Input type="date" value={d.data_proxima_interna || ''}
                onChange={(e) => set('data_proxima_interna', e.target.value)} /></Col>
            {!soInterna && (
              <Col span={6}><Text>Prazo externo (meses)</Text>
                <InputNumber value={d.prazo_externo_meses} style={{ width: '100%' }}
                  onChange={(v) => set('prazo_externo_meses', v)} decimalSeparator="," /></Col>)}
            <Col span={6}><Text>Prazo interno (meses)</Text>
              <InputNumber value={d.prazo_interno_meses} style={{ width: '100%' }}
                onChange={(v) => set('prazo_interno_meses', v)} decimalSeparator="," /></Col>
            <Col span={6}><Text>Taxa de corrosão (mm/ano)</Text>
              <InputNumber value={d.taxa_corrosao_mm_ano} step={0.0001} style={{ width: '100%' }}
                onChange={(v) => set('taxa_corrosao_mm_ano', v)} decimalSeparator="," />
              {aj('Preenchido pela etapa Vida remanescente.')}</Col>
            <Col span={6}><Text>Vida remanescente (anos)</Text>
              <InputNumber value={d.vida_remanescente_anos} step={0.1} style={{ width: '100%' }}
                onChange={(v) => set('vida_remanescente_anos', v)} decimalSeparator="," /></Col>
            <Col span={24}><Text>Parecer</Text>
              <Input.TextArea rows={3} value={d.parecer} onChange={(e) => set('parecer', e.target.value)} /></Col>
            <Col span={24}><Text>Recomendações</Text>
              <Input.TextArea rows={3} value={d.recomendacoes} onChange={(e) => set('recomendacoes', e.target.value)} /></Col>
          </Row>
        </Card>)}

      {step === 7 && barraCalculo()}

      <Space style={{ marginTop: 16 }}>
        <Button disabled={step === 0} onClick={() => setStep(step - 1)}>Anterior</Button>
        <Button disabled={step === etapas.length - 1} onClick={() => setStep(step + 1)}>Próxima</Button>
        <Button type="primary" icon={<SaveOutlined />} loading={salvando} onClick={salvar}>Salvar</Button>
        <Button icon={<FilePdfOutlined />} onClick={async () => {
        try {
        const r = await api.get('/inspecoes-camara/' + id + '/pdf', { responseType: 'blob' });
        window.open(URL.createObjectURL(r.data), '_blank');
        } catch (e) { message.error('Erro ao gerar PDF'); }
        }}>Gerar PDF</Button>
      </Space>
    </div>);
}
